import { JSX, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AccuracyReportsContext } from "../import/AccuracyReportsContext";
import { reportFormatOptions, WizardStepProps, frequencyOptions } from "../import/interfaces";
import SharedUsers from "@app/reports/SharedUsers";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import PageSection from "@app/shared/PageSection";
import { QESCard } from "@app/shared/qes-standards/Card";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import Roles from "@app/user/roles";

const SHARED_USERS_REQUIRED_ROLES = [Roles.ACCURACY_LICENSE_TIER_2];

const AccuracyReportsConfig = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(AccuracyReportsContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const isScheduledReportsEnabled = featureFlagContext.enabledFeatureFlags.has(Features.ACCURACY_SCHEDULED_REPORTS);

  const [timeValidationError, setTimeValidationError] = useState<string>("");

  const validateDateTime = () => {
    if (!wizardContext.scheduleReportCreation) {
      setTimeValidationError("");
      return true;
    }

    if (!wizardContext.startDate || !wizardContext.startTime) {
      setTimeValidationError("");
      return false;
    }

    const now = new Date();
    const [hours, minutes] = wizardContext.startTime.split(':').map(Number);

    const [year, month, day] = wizardContext.startDate.split('-').map(Number);
    const selectedDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);

    if (selectedDateTime.toDateString() === now.toDateString() && selectedDateTime <= now) {
      setTimeValidationError("The selected time has already passed. Please select the current time or a future time.");
      return false;
    }

    setTimeValidationError("");
    return true;
  };

  const getTodaysDate = () => {
    return new Date().toISOString().slice(0, 10);
  };

  useEffect(() => {
    const isValidReportName = wizardContext.reportName !== "";
    const isValidScheduling = !wizardContext.scheduleReportCreation ||
      (wizardContext.startDate !== "" && wizardContext.startTime !== "" && validateDateTime());
    props.setPageIsValid?.(isValidReportName && isValidScheduling);
  }, [wizardContext.reportName, wizardContext.scheduleReportCreation, wizardContext.startDate, wizardContext.startTime]);

  useEffect(() => {
    validateDateTime();
  }, [wizardContext.startDate, wizardContext.startTime, wizardContext.scheduleReportCreation]);

  const reportFormatRadioButtons = useMemo(() => {
    const options = reportFormatOptions.map((opt) => {
      const key = opt.toLowerCase().replaceAll(" ", "-");
      return (
        <QESRadioButton
          key={opt}
          label={opt}
          testId={`accuracy-reports__report-format--${key}`}
          onChange={() => { wizardContext.setReportFormat(opt) }}
          checked={wizardContext.reportFormat === opt}
          name="report-format"
          required={true}
        />
      );
    });
    return options;
  }, [reportFormatOptions, wizardContext.reportFormat]);

  const [alertWhenReadyLabel, setAlertWhenReadyLabel] = useState("Alert me when the report is ready to view");
  const updateSharedUsers = useCallback((sharedUsers: number[]) => {
    wizardContext.setSharedUsers(sharedUsers);
    setAlertWhenReadyLabel(
      (!sharedUsers || sharedUsers?.length === 0)
        ? "Alert me when the report is ready to view."
        : `Alert me and the other user${sharedUsers.length > 1 ? "s" : ""} when the report is ready to view.`);
  }, [wizardContext.setSharedUsers]);

  return (
    <PageSection>
      <QESCard title="Report" classNames="ar__reports-config-card mb-5">
        <div className="row">
          <div className="col-5 d-flex flex-column">
            <QESTextInput
              label="Report Name"
              required={true}
              isValid={wizardContext.reportName.length > 0}
              value={wizardContext.reportName}
              onChange={(e) => wizardContext.setReportName(e.target.value)}
              testId="accuracy-reports__report-name"
            />
            <div className="d-flex flex-row justify-content-between">
              <QESRadioGroup
                groupLabel="Report Type"
                groupContainerClass="mt-4"
                required={true}
              >
                {reportFormatRadioButtons}
              </QESRadioGroup>
            </div>
          </div>
          <div className="col-5 offset-1">
            <SharedUsers
              exportType="AccuracyActionableProvider"
              customProjectIds={wizardContext.selectedProjects.map(p => p.customProjectId)}
              tooltipTitle="The user list has been filtered to only show users with access to the information contained in this report."
              updateSelectedSharedUsers={updateSharedUsers}
              requiredRoles={SHARED_USERS_REQUIRED_ROLES}
            />
            <QESCheckbox
              label={alertWhenReadyLabel}
              onChange={(e) => wizardContext.setAlertWhenReady(e.target.checked)}
              testId="accuracy-reports__alert-when-ready"
              checked={wizardContext.alertWhenReady}
            />
          </div>
        </div>
      </QESCard>

      <AccordionContainer>
        <Accordion
          title={`Projects: ${wizardContext.selectedProjects.length}`}
          testId="accuracy-reports-config__projects-accordion"
        >
          <ul>
            {wizardContext.selectedProjects.map((p) => <li key={p.customProjectId}>{p.name}</li>)}
          </ul>
        </Accordion>
      </AccordionContainer>

      {isScheduledReportsEnabled && (
        <QESCheckbox
          label="Schedule report creation"
          onChange={(e) => wizardContext.setScheduleReportCreation(e.target.checked)}
          testId="accuracy-reports__schedule-report-creation"
          checked={wizardContext.scheduleReportCreation}
          containerClassName="mt-4"
        />
      )}

      {isScheduledReportsEnabled && wizardContext.scheduleReportCreation && (
        <div className="mt-3">
          <p className="mb-3">Schedule report creation by configuring the settings below.</p>
          <div className="col-2">
            <QESTextInput
              label="Start Date"
              type="date"
              value={wizardContext.startDate}
              onChange={(e) => wizardContext.setStartDate(e.target.value)}
              testId="accuracy-reports__start-date"
              containerClassName="mb-3"
              min={getTodaysDate()}
            />
          </div>
          <div className="col-2">
            <QESTextInput
              label="Start Time"
              type="time"
              value={wizardContext.startTime}
              onChange={(e) => wizardContext.setStartTime(e.target.value)}
              testId="accuracy-reports__start-time"
              containerClassName="mb-3"
              isValid={timeValidationError === ""}
              errorText={timeValidationError}
              unrequiredValidation={timeValidationError !== ""}
            />
          </div>
          <div className="col-2">
            <QESSelect
              label="Frequency"
              value={wizardContext.frequency}
              onChange={(e) => wizardContext.setFrequency(e.target.value as "One-Time" | "Daily" | "Weekly" | "Monthly")}
              testId="accuracy-reports__frequency"
              containerClassName="mb-3"
            >
              {frequencyOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </QESSelect>
          </div>
        </div>
      )}
    </PageSection>
  );
};

export default AccuracyReportsConfig;